import { useEffect, useState } from 'react'
import Message from '../../model/Message'
import { dataSource } from '../../service/domain/_service.config'

export function useMessageSubscription(teamID: string | undefined) {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(Boolean(teamID))
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    if (!teamID) {
      setMessages([])
      setIsLoading(false)
      setIsError(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setIsError(false)
    setMessages((current) => current.filter((message) => message.teamID === teamID))

    dataSource
      .getMessages(teamID)
      .then((fetchedMessages) => {
        if (cancelled) return
        setMessages((current) =>
          mergeMessages(
            current.filter((message) => message.teamID === teamID),
            fetchedMessages,
          ),
        )
        setIsLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setIsLoading(false)
        setIsError(true)
      })

    const subscription = dataSource.onNewMessage(teamID, (newMessage: Message) => {
      setMessages((current) => mergeMessages(current, [newMessage]))
    })

    return () => {
      cancelled = true
      subscription?.unsubscribe?.()
    }
  }, [teamID])

  function addMessage(message: Message) {
    setMessages((current) => mergeMessages(current, [message]))
  }

  return { messages, isLoading, isError, addMessage }
}

function mergeMessages(current: Message[], incoming: Message[]): Message[] {
  const seen = new Set(current.map((message) => message.id))
  const added = incoming.filter((message) => !seen.has(message.id))
  if (added.length === 0) return current
  return [...current, ...added].sort(
    (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
  )
}
